import { ReactNode, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Grid, IconButton, Checkbox, Box } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';
import { FieldArray, useFormikContext } from 'formik';
import * as yup from 'yup';
import { useAppSelector, useAppDispatch } from '../../store';
import { AdviceStatuses, CountryResidedDto, AdviceDelivery, Product } from '../../api';
import { Client, countries, ScreenProps, SendApplicationToClientResult, yearsResided } from '../../common/types';
import { Autocomplete, ListBase, Select, TextAreaInput } from '../../components/controls/inputs';
import { FormLabel, SubtitleText, TitleText, ProductIcon, Colors } from '../../components/controls/helpers';
import FormikWrapper from '../containers/FormikWrapper';
import { clearClient, clearUpsertClientResult, upsertClientAsync, getClientAsync, saveClient, setMessage } from '../../store/adviser';
import { updateAsync } from '../../store/clientApplication';
import SendApplicationToClient from './SendApplicationToClient';
import { hasSelectedProductRoles } from '../adviser/Helpers';

enum FormMode {
    None,
    Confirm,
    Submit,
    Send,
    Get
}

const addTheToCountryName = (country: string) => (([
    'Bahamas',
    'British Virgin Islands',
    'Cayman Islands',
    'Cook Islands',
    'Congo',
    'Dominican Republic',
    'Falkland Islands',
    'Faroe Islands',
    'French West Indies',
    'Maldives',
    'Marshall Islands',
    'Netherlands',
    'Netherlands Antilles',
    'Philippines',
    'Solomon Islands',
    'United Arab Emirates',
    'United Kingdom',
    'United States of America',
    'Vatican City',
    'Virgin Islands (US)'
].includes(country) ? 'The ' : '') + country);

const clientDescriptionOfConnectionsSchema = yup.object({
    countriesResided: yup.array().of(yup.object({
        yearsResided: yup.string().required('Years lived is required')
    })),
    assetsOther: yup.string()
        .when('assetsInNewZealand', { is: true, then: yup.string().required('A response is required') })
        .when('assetsOverseas', { is: true, then: yup.string().required('A response is required') }),
    businessInterestDetails: yup.string()
        .when('businessInterestInNewZealand', { is: true, then: yup.string().required('A response is required') })
        .when('businessInterestOverseas', { is: true, then: yup.string().required('A response is required') }),
    familyDetails: yup.string()
        .when('familyInNewZealand', { is: true, then: yup.string().required('A response is required') })
        .when('familyOverseas', { is: true, then: yup.string().required('A response is required') })
});

const DescriptionOfConnections = ({ inBrowserApp, match, nextRoute, prevRoute, route, history }: RouteComponentProps & ScreenProps) => {
    const { client: adviserClient, upsertClientResult } = useAppSelector(state => state.adviser);
    const { client } = useAppSelector(state => state.clientApplication);
    const _client = adviserClient || client;
    const dispatch = useAppDispatch();
    const clientId = (match.params as { clientId: string }).clientId;
    const [addingCountryResided, setAddingCountryResided] = useState(false);
    const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
    const [prevScreen, setPrevScreen] = useState(false);
    const { user } = useAppSelector(state => state.oidc);

    const initialValues = () => {
        const countriesResided = [..._client?.countriesResided || []] as CountryResidedDto[];
        if (_client?.countryOfBirth && !_client.countriesResided?.some(c => c.country === _client.countryOfBirth)) countriesResided.unshift({ country: _client.countryOfBirth, yearsResided: '' });
        if (_client?.citizenships?.length) _client.citizenships.forEach(c => !countriesResided.some(cr => cr.country === c) && countriesResided.push({ country: c, yearsResided: '' }));
        if (!countriesResided.some(c => c.country === 'New Zealand')) countriesResided.push({ country: 'New Zealand', yearsResided: '' })

        return {
            assetsOther: undefined,
            assetsInNewZealand: false,
            assetsOverseas: false,
            businessInterestDetails: undefined,
            businessInterestInNewZealand: false,
            businessInterestOverseas: false,
            familyDetails: undefined,
            familyInNewZealand: false,
            familyOverseas: false,
            ..._client,
            countriesResided
        };
    }

    const [countryOutsideNZ] = useState(() => {
        const firstNonNZCountry = initialValues().countriesResided.find(value => value.country !== 'New Zealand')?.country;
        if (!firstNonNZCountry) return 'New Zealand';
        return addTheToCountryName(firstNonNZCountry);
    });

    const cleanValues = (values: Client) => ({ ...values, countriesResided: values.countriesResided!.filter(c => !!c.country) });

    const openSendToClient = (values: Client) => {
        dispatch(saveClient(cleanValues(values)));
        setFormMode(FormMode.Confirm);
    }

    const sendToClient = (result: SendApplicationToClientResult) => {
        dispatch(upsertClientAsync({ ..._client, ...result }));
        setFormMode(FormMode.Send);
    }

    const handleSubmit = (values: Client) => {
        const _values = cleanValues(values);

        if (!inBrowserApp) {
            dispatch(saveClient(_values));

            if (formMode === FormMode.None) {
                if (values?.adviceDelivery === AdviceDelivery.InPerson) {
                    dispatch(upsertClientAsync(_values));
                    setFormMode(FormMode.Submit);
                }
                else prevScreen ? prevRoute && prevRoute(_values) : nextRoute(_values);
            }
        } else {
            dispatch(updateAsync({ token: clientId, client: { ..._values, ...route && { progress: route } } })).then((result: any) => {
                if (!result.error) nextRoute(_values);
            });
        }
    }

    useEffect(() => {
        if (formMode === FormMode.Submit) {
            if (upsertClientResult?.clientId) {
                (async () => {
                    dispatch(clearUpsertClientResult());
                    await dispatch(getClientAsync(clientId));
                    setFormMode(FormMode.Get);
                })();
            }
        } else if (formMode === FormMode.Get) {
            if (_client?.clientId) {
                setFormMode(FormMode.None);
                prevScreen ? prevRoute && prevRoute(_client) : nextRoute(_client);
            }
        } else if (formMode === FormMode.Send) {
            if (upsertClientResult?.clientId) {
                setFormMode(FormMode.None);
                dispatch(clearClient());
                dispatch(setMessage({ text: 'The application has been sent successfully.' }));
                history.replace(`/adviser/${clientId}/client-application`);
            }
        }
    }, [_client, upsertClientResult, formMode]);

    return <>
        <FormikWrapper
            initialValues={initialValues()}
            validationSchema={inBrowserApp && clientDescriptionOfConnectionsSchema}
            onSubmit={handleSubmit}>
            {({ isValid, values }) => <>
                {!inBrowserApp && <>
                    <TitleText hideMarginBottom>Description of connections</TitleText>
                    <SubtitleText>
                        <Grid container justifyContent="space-between">
                            <Grid item style={{ paddingTop: 3 }}>
                                <span style={{ color: Colors.Black }}>{values.firstName} {values.lastName || ''} </span>&emsp;{values.adviceStatus === AdviceStatuses.Skipped ? 'Product onboarding' : 'Savings Advice Tool'}
                            </Grid>
                            <Grid item>
                                {values.products?.includes(Product.KiwiSaver) && <ProductIcon product={Product.KiwiSaver} highlighted />}
                                {values.products?.includes(Product.WealthBuilder) && <ProductIcon product={Product.WealthBuilder} highlighted />}
                                {values.products?.includes(Product.IncomeGenerator) && <ProductIcon product={Product.IncomeGenerator} highlighted />}
                            </Grid>
                        </Grid>
                    </SubtitleText>
                </>}
                <Box mb={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <SubtitleText hideMarginBottom>Please answer the following for all the countries {inBrowserApp ? 'you' : 'they'} have lived in.</SubtitleText>
                        </Grid>
                        <FieldArray name="countriesResided" render={arrayHelpers => <>
                            {values.countriesResided!.map(({ country }, index) =>
                                <Grid item sm={6} xs={12} key={`countriesResided${country}${index}`}>
                                    <Grid container alignItems="flex-end" spacing={3}>
                                        <Grid item xs>
                                            <Select
                                                label={`Years living in ${country}`}
                                                name={`countriesResided[${index}].yearsResided`}
                                                options={yearsResided}
                                            />
                                        </Grid>
                                        {!(country && (values.citizenships?.some(c => c === country) || country === values.countryOfBirth) || country === 'New Zealand') &&
                                            <Grid item>
                                                <IconButton color="inherit" onClick={_ => arrayHelpers.remove(index)}>
                                                    <ClearIcon />
                                                </IconButton>
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item md={values.countriesResided!.length % 2 === 0 && !addingCountryResided ? 12 : 6} xs={12}>
                                {!addingCountryResided ?
                                    <Grid container justifyContent="flex-end" alignItems="flex-end" style={{ height: '100%' }}>
                                        <Button variant="contained" color="primary" onClick={_ => setAddingCountryResided(true)}>Add country lived in</Button>
                                    </Grid>
                                    : <Grid container alignItems="flex-end" spacing={3}>
                                        <Grid item xs>
                                            <Autocomplete
                                                label={`Add country ${inBrowserApp ? 'you' : 'they'} have lived in`}
                                                options={countries.filter(country => !values.countriesResided?.some(cr => cr.country === country))}
                                                endAdornment={<ExpandMoreIcon />}
                                                onChange={country => {
                                                    arrayHelpers.push({ country, yearsResided: '' });
                                                    setAddingCountryResided(false);
                                                }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <IconButton color="inherit" onClick={_ => setAddingCountryResided(false)}>
                                                <ClearIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                            <CheckboxListWithTextarea
                                label={`${inBrowserApp ? 'You' : 'They'} have assets (e.g. property) in`}
                                textAreaLabel="Please provide details for every answer you have ticked above"
                                textAreaName="assetsOther"
                                textAreaPlaceholder={`e.g. ${inBrowserApp ? 'you' : 'they'} own a house in ${countryOutsideNZ} and have cash assets in New Zealand.`}
                                options={[
                                    { label: 'New Zealand', name: 'assetsInNewZealand', value: values.assetsInNewZealand },
                                    { label: 'Overseas countries', name: 'assetsOverseas', value: values.assetsOverseas }
                                ]}
                            />
                            <CheckboxListWithTextarea
                                label={`${inBrowserApp ? 'You' : 'They'} have business interests in`}
                                textAreaLabel="Please provide details for every answer you have ticked above"
                                textAreaPlaceholder={`e.g. ${inBrowserApp ? 'you' : 'they'} own a florist in New Lynn, Auckland.`}
                                textAreaName="businessInterestDetails"
                                options={[
                                    { label: 'New Zealand', name: 'businessInterestInNewZealand', value: values.businessInterestInNewZealand },
                                    { label: 'Overseas countries', name: 'businessInterestOverseas', value: values.businessInterestOverseas }
                                ]}
                            />
                            <CheckboxListWithTextarea
                                label={`${inBrowserApp ? 'You' : 'They'} have family in`}
                                textAreaLabel="Please provide details for every answer you have ticked above"
                                textAreaPlaceholder={`e.g. ${inBrowserApp ? 'your' : 'their'} parents live in ${countryOutsideNZ}, ${inBrowserApp ? 'your' : 'their'} immediate family live in Auckland.`}
                                textAreaName="familyDetails"
                                options={[
                                    { label: 'New Zealand', name: 'familyInNewZealand', value: values.familyInNewZealand },
                                    { label: 'Overseas countries', name: 'familyOverseas', value: values.familyOverseas }
                                ]}
                            />
                        </>} />
                    </Grid>
                </Box>
                <Grid container spacing={2} justifyContent="space-between">
                    <Grid item>
                        {!!prevRoute && inBrowserApp && _client?.adviceDelivery !== AdviceDelivery.InPerson && <Button variant="contained" type="button" onClick={() => prevRoute(values)}>Previous</Button>}
                        {!!prevRoute && !inBrowserApp && <Button variant="contained" type="submit" disabled={formMode !== FormMode.None} onClick={() => setPrevScreen(true)}>Previous</Button>}
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2}>
                            {!inBrowserApp && _client?.adviceDelivery === AdviceDelivery.InPerson && hasSelectedProductRoles(user, values?.products) &&
                                <Grid item>
                                    <Button variant="contained" type="button" disabled={formMode !== FormMode.None} onClick={() => isValid && openSendToClient(values)}>Send to client</Button>
                                </Grid>
                            }
                            <Grid item>
                                <Button variant="contained" color="primary" type="submit" disabled={formMode !== FormMode.None}>Next</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </>}
        </FormikWrapper>
        <SendApplicationToClient hasMobile={!!_client?.mobile} open={formMode === FormMode.Confirm} cancel={() => setFormMode(FormMode.None)} ok={sendToClient} />
    </>
}

interface CheckboxListOption {
    label: string | ReactNode;
    name: string;
    value?: boolean;
    onChange?: (checked: boolean) => void;
}

const CheckboxListWithTextarea = ({ label, textAreaPlaceholder, textAreaLabel, textAreaName, showTextArea, options }: {
    label?: string | ReactNode;
    textAreaLabel?: string;
    textAreaName: string;
    textAreaPlaceholder?: string;
    showTextArea?: boolean;
    options: CheckboxListOption[];
}) => {
    const { setFieldValue, setFieldTouched } = useFormikContext();

    return options.length > 0 ? <>
        <Grid item xs={12}>
            <FormLabel label={label} />
            <ListBase
                horizontal
                options={options.map(option => ({
                    label: option.label,
                    control: <Checkbox
                        color="primary"
                        checked={option.value}
                        onChange={(_, checked) => {
                            setFieldValue(option.name, checked);
                            setFieldTouched(option.name, true, true);
                            option.onChange && option.onChange(checked);
                        }}
                    />
                }))}
            />
        </Grid>
        {(showTextArea || options.some(option => option.value)) &&
            <Grid item xs={12}>
                <TextAreaInput label={textAreaLabel} name={textAreaName} placeholder={textAreaPlaceholder} />
            </Grid>
        }
    </> : null;
}

export default DescriptionOfConnections;
